import { useSelector } from "react-redux";
import { getUserDetailsFromReducer } from "Store/reducers/Auth/AuthSlice";
import {
  hasFeatureAccess,
  getUserAccessibleFeatures,
  getPremiumFeatures,
  getComingSoonFeatures,
} from "Utility/featureUtils";

/**
 * Custom hook for feature access management
 * @returns {Object} Feature access utilities and user data
 */
export const useFeatureAccess = () => {
  const userData = useSelector(getUserDetailsFromReducer);
  const userFeatures = userData?.paidWidgets || [];

  // Check trial status
  const userType = userData?.userType;
  const isPaidUser = userData?.isPaidUser; // Legacy check

  const isTrialValid = () => {
    // If userType is trial, check dates
    if (userType === "trial") {
      const nextBillingAt = userData?.nextBillingAt;
      if (!nextBillingAt) return false;
      return new Date(nextBillingAt) > new Date();
    }
    return false;
  };

  const hasFullAccess =
    userType === "paid" || userType === "lifetime" || isPaidUser === true;

  // Create effective features list including trial features
  let effectiveUserFeatures = [...userFeatures];

  // If user is on trial and trial is expired, they shouldn't have access to paid widgets
  if ((userType === "trial" && !isTrialValid()) || userType === "free") {
    effectiveUserFeatures = [];
  }

  if ((isTrialValid() || hasFullAccess) && userType !== "free") {
    if (!effectiveUserFeatures.includes("review-generator"))
      effectiveUserFeatures.push("review-generator");
    if (!effectiveUserFeatures.includes("seo"))
      effectiveUserFeatures.push("seo");
  }

  /**
   * Check if user has access to a specific feature
   * @param {string} featureKey - The feature key to check
   * @returns {boolean} - Whether the user has access to the feature
   */
  const hasAccess = (featureKey) => {
    return hasFeatureAccess(featureKey, effectiveUserFeatures);
  };

  /**
   * Get all features the user can access
   * @returns {Array} - Array of feature objects the user can access
   */
  const getAccessibleFeatures = () => {
    return getUserAccessibleFeatures(effectiveUserFeatures);
  };

  /**
   * Get premium features that require purchase
   * @returns {Array} - Array of premium feature objects
   */
  const getPremiumFeaturesList = () => {
    return getPremiumFeatures();
  };

  /**
   * Get coming soon features
   * @returns {Array} - Array of coming soon feature objects
   */
  const getComingSoonFeaturesList = () => {
    return getComingSoonFeatures();
  };

  /**
   * Get user's purchased features
   * @returns {Array} - Array of feature keys the user has purchased
   */
  const getPurchasedFeatures = () => {
    return effectiveUserFeatures;
  };

  /**
   * Check if user has any premium features
   * @returns {boolean} - Whether the user has any premium features
   */
  const hasAnyPremiumFeatures = () => {
    return effectiveUserFeatures.length > 0;
  };

  return {
    userData,
    userFeatures: effectiveUserFeatures, // Return effective features
    hasAccess,
    getAccessibleFeatures,
    getPremiumFeaturesList,
    getComingSoonFeaturesList,
    getPurchasedFeatures,
    hasAnyPremiumFeatures,
    isTrialValid: isTrialValid(),
    nextBillingAt: userData?.nextBillingAt,
    userType,
  };
};
